import { useAdminAuth } from '../useAdminAuth'
import { useAdminAppBootstrap } from '../useAdminAppBootstrap'
import { useAdminSectionLifecycle } from '../useAdminSectionLifecycle'
import { useAdminSectionRouting } from '../useAdminSectionRouting'
import { useAdminAppManagers } from '../useAdminAppManagers'
import { useAdminAppState } from '../useAdminAppState'
import { isCustomerOnlyCapabilities } from '../bootstrapPermissions'

type Translator = (key: string, params?: Record<string, string | number>) => string

type Params = {
  t: Translator
  state: ReturnType<typeof useAdminAppState>
  managers: ReturnType<typeof useAdminAppManagers>
}

export const useAdminAppOrchestration = ({ t, state, managers }: Params) => {
  const {
    setAuthLoading,
    sessionBootstrapLoading,
    token,
    setToken,
    loginForm,
    setLoginForm,
    loginStage,
    setLoginStage,
    section,
    setSection,
    requiresDeviceTrust,
    setRequiresDeviceTrust,
    selectedUserId,
    setSelectedUserId,
    selectedShopProductId,
    setSelectedShopProductId,
    resolveSectionFromUrl,
    authHeaders,
  } = state

  const { slidesManager, pagesManager, builderManager, profileManager, securityManager } = managers

  const {
    loadPages,
    pageEditorMode,
    setPageEditorMode,
    pageEditorId,
    setPageEditorId,
  } = pagesManager

  const { loadSlides } = slidesManager

  const {
    loadProfile,
    hydrateProfile,
    resetProfile,
    account,
  } = profileManager

  const {
    sessions,
    sessionsLoading,
    loadSessions,
    bootstrapSession,
    resetSecurity,
    users: teamMembers,
    roles: roleDefinitions,
    teamLoading,
    loadTeam,
  } = securityManager

  const { setBuilderPage } = builderManager

  const { handleLogin, logout } = useAdminAuth({
    t,
    token,
    loginForm,
    setLoginForm,
    authHeaders,
    setAuthLoading,
    setToken,
    loginStage,
    setLoginStage,
    setRequiresDeviceTrust,
    setSection,
    resolveSectionFromUrl,
    hydrateProfile,
    bootstrapSession,
    clearBuilderPage: () => setBuilderPage(null),
    resetProfile,
    resetSecurity,
  })

  const isAuthenticated = Boolean(token) && !sessionBootstrapLoading
  const accountCapabilities = account?.capabilities ?? []

  useAdminSectionRouting({
    isAuthenticated,
    sessionBootstrapLoading,
    section,
    setSection,
    selectedUserId,
    setSelectedUserId,
    selectedShopProductId,
    setSelectedShopProductId,
    pageEditorMode,
    setPageEditorMode,
    pageEditorId,
    setPageEditorId,
  })

  useAdminAppBootstrap({
    isAuthenticated,
    requiresDeviceTrust,
    isCustomerOnly: isCustomerOnlyCapabilities(accountCapabilities),
    userCapabilities: accountCapabilities,
    loadSlides,
    loadPages,
    loadProfile,
    loadSessions,
  })

  const { currentSession } = useAdminSectionLifecycle({
    isAuthenticated,
    section,
    sessions,
    sessionsLoading,
    loadSessions,
    teamMembers,
    roleDefinitions,
    teamLoading,
    loadTeam,
  })

  return {
    isAuthenticated,
    handleLogin,
    logout,
    currentSession,
  }
}
